import { Fragment, useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@mui/icons-material'
import { Link as RouterLink } from 'react-router-dom'
import Link from '@mui/material/Link'
import { useTranslation } from 'react-i18next'
import { lessonSubscriptionsApi } from '@/api/lessonSubscriptions'
import type {
  LessonSubscriptionConsumption,
  LessonSubscriptionGrant,
  LessonSubscriptionGrantDisplayState,
} from '@/types/billing'
import { ROUTES } from '@/utils/constants'
import { formatDateDMY, formatDateTimeDMY } from '@/utils/date'
import { getErrorMessage } from '@/utils/error'
import { formatMoney } from './formatMoney'

type Props = {
  contractId: number
  /** id ученика по договору — для колонки «Кем выдан» */
  studentId?: number
  /** Без действий (ученик): нет кнопки «Отменить» */
  readOnly?: boolean
  /** Инкремент извне → перезагрузка списка (после выдачи абонемента, realtime) */
  refreshKey?: number
  /** Колбэк после успешной отмены grant (обновить баланс/транзакции у родителя) */
  onChanged?: () => void
}

const PER_PAGE = 10

const STATE_CHIP_COLOR: Record<
  LessonSubscriptionGrantDisplayState,
  'success' | 'info' | 'default' | 'warning' | 'error'
> = {
  active: 'success',
  pending_start: 'info',
  exhausted: 'default',
  expired: 'warning',
  canceled: 'error',
}

export function SubscriptionsBlock({ contractId, studentId, readOnly = false, refreshKey = 0, onChanged }: Props) {
  const { t } = useTranslation()
  const [grants, setGrants] = useState<LessonSubscriptionGrant[]>([])
  const [page, setPage] = useState(1)
  const [lastPage, setLastPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [consumptions, setConsumptions] = useState<Record<number, LessonSubscriptionConsumption[]>>({})
  const [consumptionsLoading, setConsumptionsLoading] = useState<number | null>(null)
  const [cancelGrantTarget, setCancelGrantTarget] = useState<LessonSubscriptionGrant | null>(null)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelSubmitting, setCancelSubmitting] = useState(false)

  const load = useCallback(
    async (pageNum: number) => {
      setLoading(true)
      setError(null)
      try {
        const res = await lessonSubscriptionsApi.listForContract(contractId, {
          page: pageNum,
          per_page: PER_PAGE,
        })
        setGrants(res.grants)
        setPage(res.meta.current_page)
        setLastPage(res.meta.last_page)
      } catch (e: unknown) {
        setError(getErrorMessage(e) || t('common.error'))
      } finally {
        setLoading(false)
      }
    },
    [contractId, t]
  )

  useEffect(() => {
    setExpandedId(null)
    setConsumptions({})
    void load(1)
  }, [load, refreshKey])

  const toggleExpand = async (grant: LessonSubscriptionGrant) => {
    if (expandedId === grant.id) {
      setExpandedId(null)
      return
    }
    setExpandedId(grant.id)
    if (consumptions[grant.id]) return
    setConsumptionsLoading(grant.id)
    try {
      const list = await lessonSubscriptionsApi.listConsumptions(grant.id)
      setConsumptions((prev) => ({ ...prev, [grant.id]: list }))
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('common.error'))
    } finally {
      setConsumptionsLoading(null)
    }
  }

  const handleCancelGrant = async () => {
    if (!cancelGrantTarget) return
    setCancelSubmitting(true)
    try {
      await lessonSubscriptionsApi.cancelGrant(cancelGrantTarget.id, cancelReason.trim() || undefined)
      setCancelGrantTarget(null)
      setCancelReason('')
      await load(page)
      onChanged?.()
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('common.error'))
    } finally {
      setCancelSubmitting(false)
    }
  }

  const issuedByLabel = (grant: LessonSubscriptionGrant): string => {
    if (grant.created_by == null) return '—'
    if (studentId != null && grant.created_by === studentId) {
      return t('billing.issuedByStudent', 'Ученик')
    }
    return t('billing.issuedByTeacher', 'Учитель')
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('billing.subscriptions', 'Абонементы')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {!loading && grants.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('billing.noSubscriptions', 'Абонементов пока нет')}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }} />
                  <TableCell>{t('billing.plan', 'Абонемент')}</TableCell>
                  <TableCell>{t('billing.grantPeriod', 'Период')}</TableCell>
                  <TableCell>{t('billing.grantRemaining', 'Осталось')}</TableCell>
                  <TableCell>{t('billing.grantState', 'Состояние')}</TableCell>
                  <TableCell>{t('billing.grantCost', 'Стоимость')}</TableCell>
                  <TableCell>{t('billing.paymentMethod', 'Способ оплаты')}</TableCell>
                  <TableCell>{t('billing.grantIssuedBy', 'Кем выдан')}</TableCell>
                  {!readOnly && <TableCell align="right" />}
                </TableRow>
              </TableHead>
              <TableBody>
                {grants.map((grant) => {
                  const canCancel =
                    !readOnly &&
                    (grant.display_state === 'active' || grant.display_state === 'pending_start')
                  const expanded = expandedId === grant.id
                  const grantConsumptions = consumptions[grant.id] ?? []
                  const colSpan = readOnly ? 8 : 9
                  return (
                    <Fragment key={grant.id}>
                      <TableRow hover>
                        <TableCell sx={{ width: 40 }}>
                          <IconButton size="small" onClick={() => void toggleExpand(grant)}>
                            {expanded ? (
                              <KeyboardArrowUpIcon fontSize="small" />
                            ) : (
                              <KeyboardArrowDownIcon fontSize="small" />
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell>{grant.plan?.title ?? `#${grant.plan_id}`}</TableCell>
                        <TableCell>
                          {formatDateDMY(grant.starts_at)} — {formatDateDMY(grant.expires_at)}
                        </TableCell>
                        <TableCell>
                          {t('billing.lessonsRemaining', '{{remaining}} из {{total}}', {
                            remaining: grant.lessons_remaining,
                            total: grant.lessons_total,
                          })}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={STATE_CHIP_COLOR[grant.display_state] ?? 'default'}
                            label={t(`billing.displayState.${grant.display_state}`, grant.display_state)}
                          />
                        </TableCell>
                        <TableCell>
                          {grant.plan?.total_price != null
                            ? formatMoney(grant.plan.total_price, grant.plan.currency)
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {grant.payment_method
                            ? t(`billing.paymentMethodOption.${grant.payment_method}`, grant.payment_method)
                            : '—'}
                        </TableCell>
                        <TableCell>{issuedByLabel(grant)}</TableCell>
                        {!readOnly && (
                          <TableCell align="right">
                            {canCancel && (
                              <Button
                                size="small"
                                color="error"
                                onClick={() => {
                                  setCancelReason('')
                                  setCancelGrantTarget(grant)
                                }}
                              >
                                {t('billing.cancelGrant', 'Отменить')}
                              </Button>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={colSpan} sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }}>
                          <Collapse in={expanded} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 1.5, pl: 5 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                {t('billing.grantConsumptions', 'Списания')}
                              </Typography>
                              {grant.canceled_at && (
                                <Typography variant="caption" color="error" display="block" sx={{ mb: 1 }}>
                                  {t('billing.canceledAt', 'Отменён')}: {formatDateDMY(grant.canceled_at)}
                                  {grant.canceled_reason ? ` — ${grant.canceled_reason}` : ''}
                                </Typography>
                              )}
                              {consumptionsLoading === grant.id ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <CircularProgress size={16} />
                                  <Typography variant="body2" color="text.secondary">
                                    {t('common.loading', 'Загрузка…')}
                                  </Typography>
                                </Box>
                              ) : grantConsumptions.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                  {t('billing.noConsumptions', 'Списаний пока нет')}
                                </Typography>
                              ) : (
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>{t('billing.consumedAt', 'Дата списания')}</TableCell>
                                      <TableCell>{t('billing.lessonLink', 'Урок')}</TableCell>
                                      <TableCell>{t('billing.reverted', 'Возврат')}</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {grantConsumptions.map((c) => (
                                      <TableRow key={c.id}>
                                        <TableCell>
                                          {formatDateTimeDMY(c.consumed_at)}
                                        </TableCell>
                                        <TableCell>
                                          {c.lesson_id != null ? (
                                            <Link
                                              component={RouterLink}
                                              to={ROUTES.LESSON_DETAILS(c.lesson_id)}
                                              underline="hover"
                                            >
                                              {t('lessons.detailsTitle', 'Урок #{{id}}', { id: c.lesson_id })}
                                            </Link>
                                          ) : (
                                            '—'
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {c.reverted_at ? (
                                            <Chip
                                              size="small"
                                              color="warning"
                                              label={`${t('billing.reverted', 'Возврат')}: ${formatDateDMY(c.reverted_at)}`}
                                            />
                                          ) : (
                                            '—'
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {lastPage > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              size="small"
              count={lastPage}
              page={page}
              onChange={(_e, value) => void load(value)}
            />
          </Box>
        )}
      </CardContent>

      <Dialog
        open={cancelGrantTarget !== null}
        onClose={() => !cancelSubmitting && setCancelGrantTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('billing.cancelGrantTitle', 'Отменить абонемент')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t(
              'billing.cancelGrantConfirm',
              'Абонемент «{{plan}}» будет отменён, оставшиеся занятия станут недоступны.',
              { plan: cancelGrantTarget?.plan?.title ?? `#${cancelGrantTarget?.plan_id}` }
            )}
          </DialogContentText>
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            label={t('billing.cancelGrantReason', 'Причина отмены')}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelGrantTarget(null)} disabled={cancelSubmitting}>
            {t('common.cancel', 'Отмена')}
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={cancelSubmitting}
            onClick={() => void handleCancelGrant()}
          >
            {t('billing.cancelGrant', 'Отменить')}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}
